import { MouseEvent, ReactNode, useCallback } from 'react';
import Button from '~/commonComponents/Button';
import { Center } from '~/commonComponents/Center';
import { Column } from '~/commonComponents/Column';
import CustomSpinner from '~/commonComponents/CustomSpinner';
import { Pressable } from '~/commonComponents/Pressable';
import { Text } from '~/commonComponents/Text';
import { UserProfession } from '~/enums/UserProfession';
import { useAppSelector } from '~/hooks/useAppSelector';
import { IUser } from '~/models';
import { Colors } from '~/themes/colors';
import { ListItem } from './ListItem';

interface IProps {
  EmptyComponent: ReactNode;
  isEmpty: boolean;
  isInviting: boolean;
  isLoading: boolean;
  professionals: IUser[];
  selectedProfessionals: Set<string>;
  selectedProfession?: string;
  onClose: (event: MouseEvent<HTMLButtonElement>) => void;
  onSelectProfessionals: (ids: string[], forceAdd?: boolean) => void;
  onSentInvitations: () => void;
  hideSelectAllButton?: boolean;
  confirmButtonText?: string;
}

export const ProfessionalList = ({
  EmptyComponent,
  hideSelectAllButton = false,
  isEmpty,
  isInviting,
  isLoading,
  professionals,
  selectedProfessionals,
  selectedProfession,
  onClose,
  onSelectProfessionals,
  onSentInvitations,
  confirmButtonText = 'Sent Invites',
}: IProps) => {
  const allProfessions = useAppSelector((state) => state.user.allProfessions);
  const job = useAppSelector((state) => state.job.job);
  const profession = selectedProfession ?? job?.profession;

  const getProfession = useCallback(
    (it: IUser) => {
      const isProfessionalHasMultipleProfession = Array.isArray((it as any)?.professions);

      if (isProfessionalHasMultipleProfession) {
        return (it as any).professions?.includes(profession)
          ? allProfessions?.find(({ code }) => profession === code)?.name
          : allProfessions?.find(({ code }) => (it as any).professions[0] === code)?.name;
      }

      return UserProfession.getName((it as any).profession);
    },
    [allProfessions, profession],
  );

  if (isLoading) {
    return (
      <Center flex={1}>
        <CustomSpinner isLoading size={32} />
      </Center>
    );
  }

  if (isEmpty) {
    return (
      <Column flex={1} paddingHorizontal={48}>
        <Column flex={1} marginBottom={10}>
          {EmptyComponent}
        </Column>
        <Button text="Close" style={{ marginInline: 'auto', width: 152 }} onClick={onClose} />
      </Column>
    );
  }

  return (
    <Column flex={1}>
      {!hideSelectAllButton && (
        <Pressable
          onPress={() => {
            onSelectProfessionals(
              professionals.map((it) => it.id),
              true,
            );
          }}
          style={{ marginLeft: 'auto', marginTop: 24, padding: '0 48px' }}
        >
          <Text bold color={Colors.secondary_500} fontSize={18}>
            Select All
          </Text>
        </Pressable>
      )}
      <Column
        flex={1}
        flexBasis={0}
        overflowY="auto"
        marginBottom={32}
        marginTop={24}
        paddingHorizontal={48}
        style={{
          scrollbarWidth: 'thin',
        }}
      >
        {professionals.map((it) => {
          return (
            <ListItem
              key={it.id}
              fullName={it?.full_name}
              id={it.id}
              isAvailable={it.isAvailable || false}
              isFavorite={it.isFavorite}
              isSelected={selectedProfessionals.has(it.id)}
              profession={getProfession(it)}
              profileImage={it.profile_image}
              onItemClick={(id: string) => onSelectProfessionals([id])}
            />
          );
        })}
      </Column>
      <Button
        text={confirmButtonText}
        disabled={selectedProfessionals.size === 0}
        isLoading={isInviting && !isLoading}
        style={{ marginInline: 'auto', width: 152 }}
        onClick={onSentInvitations}
      />
    </Column>
  );
};
